import clsx from "clsx";
import React, { useState } from "react";
import { Settings as SlickSettings } from "react-slick";
import { BREAKPOINTS } from "web/react/constants";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { ProductImageGalleryCarouselEmo } from "./product-image-carousel-emo";
import FullscreenGalleryEmo from "./product-image-gallery-fullscreen-emo";
import * as styles from "./product-image-gallery.css";

export interface Image {
    id: string | number;
    altText?: string | null;
    url: string;
    src?: string;
}

function buildUrl(url: string, width: number, height: number): string {
    return url.replace("/photos/", `/${width}/${height}/n/photos/`);
}

export enum LazyLoadTypes {
    EAGER = "eager",
    LAZY = "lazy",
}

type ImageProps = {
    src: string;
    alt?: string;
    onClick: () => void;
    width?: number;
    height?: number;
    isFullscreen?: boolean;
    lazy?: boolean;
};

export const Image = ({
    src,
    alt,
    lazy = false,
    onClick,
    width = 520,
    height = 650,
    isFullscreen = false,
}: ImageProps): JSX.Element => (
    <div
        className={clsx({
            [styles.item]: !isFullscreen,
            [styles.fullScreenItem]: isFullscreen,
        })}
    >
        <img
            className={clsx({
                [styles.image]: !isFullscreen,
                [styles.fullscreenImage]: isFullscreen,
            })}
            decoding="async"
            src={buildUrl(src, width, height)}
            alt={alt}
            loading={lazy ? LazyLoadTypes.LAZY : LazyLoadTypes.EAGER}
            width={width}
            onClick={onClick}
        />
    </div>
);

type ProductImageGalleryProps = {
    images: Image[];
    analyticsAction?: string;
    currentSlideIndex?: number;
    productId: string;
};

export const ProductImageGallery = ({
    images,
    analyticsAction = "",
    productId,
}: ProductImageGalleryProps): JSX.Element => {
    const [fullscreen, setFullscreen] = useState(false);
    const [initialSlideIndex, setInitialSlideIndex] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);
    const [firstInteraction, setFirstInteraction] = useState(false);

    const { isDesktopViewport } = useDomViewport();
    const viewport = isDesktopViewport ? "desktop" : "mobile";
    const analyticsActionString = analyticsAction || `${viewport}_gallery`;

    function handleImageClick(index): void {
        setInitialSlideIndex(index);
        setCurrentSlideIndex(index);
        setFullscreen(true);
    }

    function onSlideChange(curSlide, nextSlide): void {
        setCurrentSlideIndex(nextSlide);
        if (!firstInteraction) {
            setFirstInteraction(true);
            analytics.event("engagement", analyticsAction, "interacted", false, {
                product_id: productId,
            });
        }
        analytics.event("engagement", analyticsAction, "changed_image", false, {
            product_id: productId,
        });
    }

    const afterSlideChange = (currentSlide: number): void => {
        onSlideChange(undefined, currentSlide);
    };

    // Slick settings and props for the fullscreen gallery
    const fullscreenGalleryEmoSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: initialSlideIndex,
        beforeChange: onSlideChange,
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        lazyLoad: undefined,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    const InPageViewSettings: SlickSettings = {
        accessibility: true,
        speed: 500,
        slidesToShow: 1,
        dots: images.length > 1,
        initialSlide: currentSlideIndex,
        afterChange: afterSlideChange,
        centerMode: false,
        touchThreshold: 5,
        infinite: false,
        swipeToSlide: true,
        arrows: false,
        swipe: true,
        touchMove: false,
        draggable: false,
        lazyLoad: undefined,
        responsive: [
            {
                breakpoint: BREAKPOINTS.Medium,
                settings: {
                    slidesToShow: 1,
                    speed: 200, // we need this for smooth swipe
                    touchMove: true,
                    draggable: false,
                },
            },
            {
                breakpoint: BREAKPOINTS.Large,
                settings: {
                    slidesToShow: 1,
                    speed: 200,
                    touchMove: true,
                    draggable: true,
                },
            },
        ],
    };

    const fullscreenGalleryEmoProps = {
        analyticsAction: analyticsActionString,
        isOpen: fullscreen,
        onClose: (): void => setFullscreen(false),
        images: images,
        settings: fullscreenGalleryEmoSettings,
    };

    return (
        <>
            {fullscreen ? (
                <FullscreenGalleryEmo
                    currentSlideIndex={currentSlideIndex}
                    productId={productId}
                    {...fullscreenGalleryEmoProps}
                />
            ) : (
                <ProductImageGalleryCarouselEmo
                    images={images}
                    settings={InPageViewSettings}
                    isFullscreen={fullscreen}
                    handleImageClick={(index): void => handleImageClick(index)}
                    analyticsAction={analyticsActionString}
                    onClose={(): void => setFullscreen(false)}
                    currentSlideIndex={currentSlideIndex}
                    productId={productId}
                />
            )}
        </>
    );
};
